// The icon rail's name labels for KEYBOARD users (components/Layout.js).
//
// In the icon-only rail each control already carries its name twice: an
// aria-label (what a screen reader says) and a `title` (the browser's own
// tooltip on mouse hover).  A `title` never shows for keyboard focus, so a
// sighted keyboard user tabbing down the rail saw bare icons.  This draws the
// name beside whichever rail control has keyboard focus (:focus-visible —
// not after a mouse click, where the title tooltip already covers it, and a
// second label would double up).
//
// Rail controls opt in with data-rail-tip="<name>".  It listens on the rail
// itself, so only this component re-renders while focus moves, never Layout.
// position: fixed, because the rail's scrolling list would clip a label
// hanging out of it; it hides when the list scrolls or the window resizes.

import { useEffect, useState } from 'react'

function isKeyboardFocus (el) {
  try { return el.matches(':focus-visible') } catch { return true }
}

export default function RailTooltip ({ containerRef, enabled }) {
  const [tip, setTip] = useState(null)
  useEffect(() => {
    const root = containerRef && containerRef.current
    if (!enabled || !root) { setTip(null); return undefined }
    const show = (e) => {
      const el = e.target && typeof e.target.closest === 'function' ? e.target.closest('[data-rail-tip]') : null
      if (!el || !root.contains(el) || !isKeyboardFocus(el)) { setTip(null); return }
      const r = el.getBoundingClientRect()
      setTip({ label: el.getAttribute('data-rail-tip') || '', top: r.top + r.height / 2, left: r.right + 8 })
    }
    const hide = () => setTip(null)
    root.addEventListener('focusin', show)
    root.addEventListener('focusout', hide)
    root.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)
    return () => {
      root.removeEventListener('focusin', show)
      root.removeEventListener('focusout', hide)
      root.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [enabled, containerRef])

  if (!enabled || !tip || !tip.label) return null
  return (
    <div
      data-testid="rail-tooltip"
      aria-hidden="true"
      className="no-print fixed z-[70] pointer-events-none whitespace-nowrap rounded-md bg-gray-900 text-white text-xs font-medium px-2 py-1 shadow-lg"
      style={{ top: tip.top, left: tip.left, transform: 'translateY(-50%)' }}>
      {tip.label}
    </div>
  )
}
